import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../firebase/AuthContext';
import { useFirebase } from '../firebase/FirebaseContext';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';

const VoiceModelDetails = () => {
    const { modelId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { db } = useFirebase();
    const [model, setModel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showPauseConfirm, setShowPauseConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [pausing, setPausing] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        const fetchModelDetails = async () => {
            if (!user || !modelId) return;

            try {
                const docRef = doc(db, 'voice_models', modelId);
                const docSnap = await getDoc(docRef);

                if (!docSnap.exists()) {
                    setError('Voice model not found');
                    return;
                }

                const data = docSnap.data();
                if (data.userId !== user.uid) {
                    setError('You do not have permission to view this model');
                    return;
                }

                setModel({
                    id: docSnap.id,
                    ...data,
                    createdAt: data.createdAt?.toDate()
                });
            } catch (error) {
                console.error('Error fetching model details:', error);
                setError('Failed to load model details');
            } finally {
                setLoading(false);
            }
        };

        fetchModelDetails();
    }, [user, modelId, db]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'training':
                return 'bg-blue-100 text-blue-800';
            case 'completed':
                return 'bg-green-100 text-green-800';
            case 'failed':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const handlePauseTraining = async () => {
        if (!model || model.status !== 'training') return;
        
        setPausing(true);
        try {
            const modelRef = doc(db, 'voice_models', modelId);
            await updateDoc(modelRef, {
                status: 'paused'
            });
            
            setModel(prev => ({
                ...prev,
                status: 'paused'
            }));
            setShowPauseConfirm(false);
        } catch (error) {
            console.error('Error pausing model training:', error);
            setError('Failed to pause model training');
        } finally {
            setPausing(false);
        }
    };

    const handleDeleteModel = async () => {
        if (!model) return;
        
        setDeleting(true);
        try {
            const modelRef = doc(db, 'voice_models', modelId);
            await updateDoc(modelRef, {
                isDeleted: true,
                deletedAt: new Date()
            });
            navigate('/fine-tuning');
        } catch (error) {
            console.error('Error deleting model:', error);
            setError('Failed to delete model');
            setShowDeleteConfirm(false);
        } finally {
            setDeleting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                        <p className="mt-2 text-gray-500">Loading model details...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-white rounded-lg shadow-sm p-8">
                        <div className="text-center">
                            <p className="text-red-600 mb-4">{error}</p>
                            <button
                                onClick={() => navigate('/fine-tuning')}
                                className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800"
                            >
                                Back to Voice Models
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                <div className="bg-white rounded-lg shadow-sm p-8">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h3 className="text-2xl font-medium text-gray-900 mb-2">
                                Voice Model Details
                            </h3>
                            <p className="text-gray-500">View and manage your custom voice model</p>
                        </div>
                        <button
                            onClick={() => navigate('/fine-tuning')}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                            Back to Voice Models
                        </button>
                    </div>

                    <div className="space-y-8">
                        {/* Basic Information */}
                        <div>
                            <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
                            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Name</span>
                                    <span className="font-medium text-gray-900">{model.name}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Description</span>
                                    <span className="font-medium text-gray-900">{model.description}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Created</span>
                                    <span className="font-medium text-gray-900">
                                        {model.createdAt?.toLocaleDateString()}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Status</span>
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(model.status)}`}>
                                        {model.status}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Training Configuration */}
                        <div>
                            <h4 className="text-lg font-medium text-gray-900 mb-4">Training Configuration</h4>
                            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Data Source</span>
                                    <span className="font-medium text-gray-900">
                                        {model.dataSource === 'zip' ? 'ZIP File' : 'HuggingFace Repository'}
                                    </span>
                                </div>
                                {model.dataSource === 'huggingface' && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-600">Repository URL</span>
                                        <a
                                            href={model.huggingfaceLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-medium text-blue-600 hover:text-blue-800"
                                        >
                                            {model.huggingfaceLink}
                                        </a>
                                    </div>
                                )}
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Audio Sample Rate</span>
                                    <span className="font-medium text-gray-900">{model.trainingConfig.audioSampleRate} Hz</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Audio Duration</span>
                                    <span className="font-medium text-gray-900">{model.trainingConfig.audioDuration} minutes</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Number of Audio Files</span>
                                    <span className="font-medium text-gray-900">{model.trainingConfig.numberOfAudioFiles}</span>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end space-x-4">
                            {model?.status === 'training' && (
                                <button
                                    onClick={() => setShowPauseConfirm(true)}
                                    disabled={pausing}
                                    className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {pausing ? 'Pausing...' : 'Pause Model Training'}
                                </button>
                            )}
                            <button
                                onClick={() => setShowDeleteConfirm(true)}
                                disabled={deleting}
                                className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {deleting ? 'Deleting...' : 'Delete Model'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pause Confirmation Dialog */}
            {showPauseConfirm && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                            Pause Model Training
                        </h3>
                        <p className="text-gray-500 mb-6">
                            Are you sure you want to pause the training of this model? This action can be resumed later.
                        </p>
                        <div className="flex justify-end space-x-4">
                            <button
                                onClick={() => setShowPauseConfirm(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handlePauseTraining}
                                disabled={pausing}
                                className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {pausing ? 'Pausing...' : 'Pause Training'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                            Delete Model
                        </h3>
                        <p className="text-gray-500 mb-6">
                            Are you sure you want to delete this model? This action cannot be undone and all associated data will be permanently removed.
                        </p>
                        <div className="flex justify-end space-x-4">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteModel}
                                disabled={deleting}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {deleting ? 'Deleting...' : 'Delete Model'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VoiceModelDetails; 